import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Play, Zap, Image, Video, Music } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const HeroSection = () => {
  const { t } = useLanguage();
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'linear-gradient(hsl(220 15% 15% / 0.5) 1px, transparent 1px), linear-gradient(90deg, hsl(220 15% 15% / 0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-pulse-glow" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[150px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-glass border border-primary/30 mb-8 animate-fade-in">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">{t("สร้างสรรค์ด้วย AI", "AI-Powered Creation")}</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-heading font-bold mb-6 leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
            {t("สร้างสรรค์", "Create")}{" "}
            <span className="text-gradient">{t("คอนเทนต์", "Stunning")}</span>
            <br />
            {t("ด้วย AI", "AI Content")}
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {t("สร้างภาพสวยงาม วิดีโอภาพยนตร์ และเพลงต้นฉบับ ด้วยพลังของปัญญาประดิษฐ์", "Generate beautiful photos, cinematic videos, and original music with the power of artificial intelligence.")}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <Link to="/register">
              <Button variant="hero" size="xl" className="gap-2">
                <Zap className="h-5 w-5" />
                {t("เริ่มสร้างฟรี", "Start Creating Free")}
              </Button>
            </Link>
            <a href="https://www.gencnx.com/example" target="_blank" rel="noopener noreferrer">
              <Button variant="glass" size="xl" className="gap-2">
                <Play className="h-5 w-5" />
                {t("ดูตัวอย่าง", "See Demo")}
              </Button>
            </a>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border">
              <Image className="h-4 w-4 text-cyan-500" />
              <span className="text-sm">{t("สร้างภาพ", "Photo Generation")}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border">
              <Video className="h-4 w-4 text-purple-500" />
              <span className="text-sm">{t("สร้างวิดีโอ", "Video Creation")}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border">
              <Music className="h-4 w-4 text-magenta-500" />
              <span className="text-sm">{t("สร้างเพลง", "Music Synthesis")}</span>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/3 left-10 animate-float hidden lg:block" style={{ animationDelay: '0s' }}>
          <div className="w-20 h-20 rounded-2xl bg-glass border border-primary/30 flex items-center justify-center glow-primary">
            <Image className="h-8 w-8 text-primary" />
          </div>
        </div>
        <div className="absolute top-1/2 right-10 animate-float hidden lg:block" style={{ animationDelay: '2s' }}>
          <div className="w-20 h-20 rounded-2xl bg-glass border border-secondary/30 flex items-center justify-center glow-secondary">
            <Video className="h-8 w-8 text-secondary" />
          </div>
        </div>
        <div className="absolute bottom-1/3 left-20 animate-float hidden lg:block" style={{ animationDelay: '4s' }}>
          <div className="w-20 h-20 rounded-2xl bg-glass border border-accent/30 flex items-center justify-center glow-accent">
            <Music className="h-8 w-8 text-accent" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
